/**
 * Named locations — Zod schemas.
 *
 * Named locations are reusable definitions referenced by
 * conditional-access policies. A single policy may allow "any
 * location except named location X" or "only named location Y";
 * this feature defines the X and Y.
 *
 * Three location kinds:
 *   - `ip_ranges`   — CIDR blocks (IPv4/IPv6)
 *   - `countries`   — ISO-3166-1 alpha-2 country codes
 *   - `trusted`     — a flagged label for internal / corporate
 *                     networks that policies treat as "trusted"
 */

import { z } from "zod";

export const namedLocationKindSchema = z.enum(["ip_ranges", "countries"]);
export type NamedLocationKind = z.infer<typeof namedLocationKindSchema>;

// One disciplined CIDR regex — IPv4 + IPv6 permissive validation.
// The backend does the authoritative parse; this catches obvious
// typos at form-submission time.
const cidrRegex =
  /^(?:[0-9]{1,3}(?:\.[0-9]{1,3}){3}(?:\/(?:3[0-2]|[12]?[0-9]))?|[0-9a-fA-F:]+(?:\/(?:1[01][0-9]|12[0-8]|[1-9]?[0-9]))?)$/;

// The country-code regex is shape-only (any uppercase A-Z 2-letter
// pair). It accepts ALL real ISO-3166-1 alpha-2 codes, the "XX"
// sentinel for GeoIP-failure opt-in, AND a long tail of two-letter
// strings that aren't real countries (ZZ, QQ, etc.). Maintaining a
// 249-entry whitelist in both the frontend Zod and the backend Go
// validator was judged worse than accepting the loose form: the
// backend's GeoIP lookup never returns a non-real code, so a
// named-location entry like "ZZ" simply never matches anything and
// the operator notices on first use. Round 2 cross-cutting F10.
const countryCodeRegex = /^[A-Z]{2}$/;

export const namedLocationSchema = z.object({
  id: z.string(),
  /** Optimistic-concurrency version. Increments on every server-side
   * update. The editor form reads this and sends it back as
   * `expected_version` on update so two admins editing the same
   * location at once get a 409 instead of silently clobbering. */
  version: z.number().int().nonnegative(),
  name: z.string(),
  /** Optional human-readable description. Operators use this for
   * the "why does this list exist" annotation that's invaluable
   * during incident response. The backend stores it as
   * `description TEXT NOT NULL DEFAULT ''` (see
   * db/migrations/000001_init.up.sql (was 27_conditional_access.sql)), so an empty string and
   * an undefined are equivalent on the wire. */
  description: z.string(),
  kind: namedLocationKindSchema,
  /** CIDR list for kind="ip_ranges", country code list for "countries".
   *
   * For kind="countries", the special sentinel "XX" matches any
   * IP whose GeoIP lookup fails. Operators who want to gate on
   * "unknown country" add "XX" to the entries list explicitly —
   * the old `include_unknown_countries` boolean was removed in
   * favour of this explicit, auditable opt-in. */
  entries: z.array(z.string()),
  /** Trusted for the tenant — policies can treat this as a "corp net". */
  trusted: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type NamedLocation = z.infer<typeof namedLocationSchema>;

export const namedLocationsListResponseSchema = z.object({
  data: z.array(namedLocationSchema),
});
export type NamedLocationsListResponse = z.infer<typeof namedLocationsListResponseSchema>;

export const createNamedLocationSchema = z
  .object({
    name: z.string().min(1, "Name is required").max(80),
    description: z.string().max(500),
    kind: namedLocationKindSchema,
    entries: z.array(z.string().min(1)).min(1, "At least one entry is required"),
    trusted: z.boolean(),
  })
  .superRefine((value, ctx) => {
    if (value.kind === "ip_ranges") {
      value.entries.forEach((entry, idx) => {
        if (!cidrRegex.test(entry.trim())) {
          ctx.addIssue({
            code: "custom",
            message: `"${entry}" is not a valid CIDR`,
            path: ["entries", idx],
          });
        }
      });
    } else if (value.kind === "countries") {
      value.entries.forEach((entry, idx) => {
        const upper = entry.trim().toUpperCase();
        // Accept the explicit "XX" GeoIP-failure sentinel
        // alongside any real ISO-3166-1 alpha-2 code. The
        // backend treats "XX" as the operator's opt-in to
        // matching IPs whose GeoIP lookup returned nothing.
        if (!countryCodeRegex.test(upper)) {
          ctx.addIssue({
            code: "custom",
            message: `"${entry}" is not a 2-letter ISO country code (use "XX" for unknown / GeoIP-lookup failures)`,
            path: ["entries", idx],
          });
        }
      });
    }
  });
export type CreateNamedLocationInput = z.infer<typeof createNamedLocationSchema>;
